import { useMemo } from 'react'
import { type CompanyData, isEmpty, type ReportKpiGrouping, type TeamData, type TeamLevel } from '@terros/common'
import { useProfile } from '../user'
import { type SelectItem } from '../types'
import { type AvailableLevels, toTeamLevelSelectOptions, useTeams } from '../team'

export type DrilldownOption = TeamLevel | 'user'

export const useGroupingOptions = (
  selectedTeamLevel?: TeamLevel,
  enabled?: boolean
): { options: SelectItem<DrilldownOption>[]; isLoading: boolean } => {
  const { company, isLoading: profileLoading } = useProfile(undefined, enabled)
  const { data: allTeams, isLoading } = useTeams({ enabled })
  const availableLevels = useMemo(() => getAvailableGroupingLevels(allTeams), [allTeams])

  const options = useMemo(
    () => toSelectOptions(company, selectedTeamLevel, availableLevels),
    [company, selectedTeamLevel, availableLevels]
  )

  return { options, isLoading: profileLoading || isLoading }
}

const getAvailableGroupingLevels = (allTeams: TeamData[] | undefined): AvailableLevels | undefined => {
  if (isEmpty(allTeams)) return undefined
  const levelCounts = allTeams.reduce((acc, team) => {
    acc.set(team.level, (acc.get(team.level) || 0) + 1)
    return acc
  }, new Map<TeamLevel, number>())
  const validGroupingLevels = Array.from(levelCounts.entries())
    .filter(([, count]) => count > 1)
    .map(([level]) => level)

  if (isEmpty(validGroupingLevels)) return

  const highestLevel = Math.max(...validGroupingLevels)
  const lowestLevel = Math.min(...validGroupingLevels)
  return { highestLevel, lowestLevel, levels: validGroupingLevels }
}

const toSelectOptions = (
  company: CompanyData | undefined,
  selectedTeamLevel: TeamLevel | undefined,
  availableLevels: AvailableLevels | undefined
): SelectItem<DrilldownOption>[] => {
  if (!company) return []

  const levels = getSelectLevels(company, selectedTeamLevel, availableLevels)
  const options: SelectItem<DrilldownOption>[] = toTeamLevelSelectOptions(company, levels)
  options.unshift({
    label: 'User',
    value: 'user',
  })
  return options
}

const getSelectLevels = (
  company: CompanyData | undefined,
  selectedTeamLevel: TeamLevel | undefined,
  availableLevels: AvailableLevels | undefined
): TeamLevel[] => {
  const levelNames = company?.levelNames
  if (!levelNames || !availableLevels) return []
  const { highestLevel, lowestLevel } = availableLevels
  const firstLevel = selectedTeamLevel ?? highestLevel
  return Array.from({ length: firstLevel - lowestLevel + 1 }).map((_, index) => firstLevel - index)
}

export const optionToGrouping = (option: DrilldownOption): ReportKpiGrouping => {
  if (option === 'user') {
    return { type: 'user' }
  }
  return { type: 'team', level: option }
}

export const groupingToOption = (grouping: ReportKpiGrouping): DrilldownOption => {
  if (grouping.type === 'team') return grouping.level
  return 'user'
}
